<script setup lang="ts">
import type { ICity } from '~/features/store';
import { ref, computed } from 'vue';
import { useCityStore } from '@/features/store';
import { MMPopupDefault, MMText } from '@mm-frontend/mm-web-ui-kit';
import { InputFilled } from '~/features/ui-kit';
import { Plural } from '~/shared/helpers/Plural';

const { cities, currentCityId, setCurrentCityId } = useCityStore();

const searchQuery = ref('');

const filteredCities = computed(() => {
  return cities.value.filter((city) =>
    city.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const handleSelectCity = (city: ICity) => {
  setCurrentCityId(city.id.toString());
  location.reload();
};

onMounted(() => {
  searchQuery.value = '';
});

const pluralCitiesCount = computed(() => {
  return Plural.with(cities.value.length, 'город|города|городов');
});
</script>

<template>
  <MMPopupDefault class="cities-modal">
    <template #header>
      <MMText font="Title M/Semibold" class="cities-modal__header-title">
        Выберите город доставки
      </MMText>
      <MMText font="Body L/Regular" class="cities-modal__header-subtitle">
        Бесплатная доставка на следующий день в {{ pluralCitiesCount }} России
      </MMText>

      <div class="cities-modal__search">
        <InputFilled
          v-model="searchQuery"
          placeholder="Введите город"
          size="l48"
          data-test-id="input__city-search"
        />
      </div>
    </template>

    <div v-if="filteredCities.length > 0" class="cities-modal__items" data-test-id="list__city">
      <div
        v-for="city in filteredCities"
        :key="city.id"
        class="cities-modal__item"
        :data-test-id="['block__city', city.id === currentCityId ? 'text__chosen' : '']"
        @click="handleSelectCity(city)"
      >
        <MMText v-if="city.id === currentCityId" font="Body S/Regular" color="tertiary">
          Выбран
        </MMText>
        <MMText font="Body M/Regular" :color="city.id === currentCityId ? 'accented' : 'primary'">
          {{ city.name }}
        </MMText>
      </div>
    </div>

    <div v-else class="cities-modal__empty">
      <MMText font="Title S/Semibold" color="primary" data-test-id="text__no-city-result">
        Город не найден
      </MMText>
      <MMText font="Body M/Regular" color="tertiary">
        Проверьте название или попробуйте выбрать другой — возможно, мы пока не работаем в этом регионе
      </MMText>
    </div>
  </MMPopupDefault>
</template>

<style lang="stylus">
.cities-modal
  width 640px !important
  min-height 520px !important
  display flex
  flex-direction column
  margin 56px 0 56px 0

  +Media(Mobile)
    height calc(100dvh - 100px)
    margin 0

  .cities-modal__header-title
    text-align center
    margin-bottom 25px

  .cities-modal__header-subtitle
    text-align center

  .cities-modal__search
    margin 24px 0 24px 0

    & input
      background-color transparent

  .cities-modal__items
    display flex
    flex-direction column
    overflow-y auto

    .cities-modal__item
      display flex
      flex-direction column
      gap 4px
      padding 12px 16px
      cursor pointer
      border-radius 12px

      &:hover
        background-color #F5F5F5

  .cities-modal__empty
    display flex
    flex-direction column
    align-items center
    gap 16px
    max-width 75%
    text-align center
    margin auto

    +Media(Mobile)
      margin-top 24px

    &-link
      cursor pointer
      text-decoration underline
</style>
